import React, { useState, useRef, useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { useClickOutside } from "../../hooks";
import {
  Dashboard,
  Organisation,
  Customer,
  Voucher,
  Subscription,
  Social,
  Loyalty,
  AdminPanel,
} from "../../icons";

export const Sidebar = ({ items }) => {
  // `selectedItem` is only which flyout is open. It is deliberately separate
  // from which group is *active*: the two were conflated through a
  // "selected-nav" sessionStorage index written on click, so merely opening a
  // group's menu marked it active for the rest of the session — the sidebar
  // could show "Organisation" highlighted while you were on the Dashboard.
  const [selectedItem, setSelectedItem] = useState(null);
  const targetEl = useRef();
  const { pathname } = useLocation();

  const handleActiveElement = useCallback((e, index) => {
    e.preventDefault();
    setSelectedItem((current) => (current === index ? null : index));
  }, []);

  /** A group is active when the current route is one of its children. */
  const isGroupActive = useCallback(
    (children = []) =>
      children.some(({ path, show }) => {
        if (!show || !path) return false;
        const target = `/${String(path).replace(/^\//, "")}`;
        return pathname === target || pathname.startsWith(`${target}/`);
      }),
    [pathname]
  );

  useClickOutside(targetEl, () => setSelectedItem(null));

  /**
   * Below 1600px the rail is an off-canvas drawer held open by `open_menu` on
   * <body> (App.js:41). Nothing ever removed that class, so tapping a menu item
   * navigated underneath a drawer that stayed open over the new page — on a
   * phone the app looked stuck. Closing on pathname change covers every route
   * transition, including the browser back button, not just the tap.
   */
  useEffect(() => {
    document.body.classList.remove("open_menu");
  }, [pathname]);

  const icons = {
    Dashboard,
    Organisation,
    Customer,
    Voucher,
    Subscription,
    Social,
    Loyalty,
    AdminPanel,
  };

  return (
    <>
      {items?.length > 0 ? (
        <nav className="sidebar">
          <div className="sidebar-sticky" ref={targetEl}>
            {items?.map(({ title, items }, index) => {
              let Icon = icons[`${title.split(" ").join("")}`];
              return (
                <div key={title}>
                  {items.filter(({ show }) => show)?.length > 0 && (
                    <ul className="navbar-nav">
                      <li
                        className={`nav-item dropdown ${
                          isGroupActive(items) ? "active" : ""
                        } ${selectedItem === index ? "open" : ""}`}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {Icon && <Icon />}
                          <Link
                            className="nav-link inner-menu-toggle"
                            to="/"
                            id="navbarDropdown"
                            role="button"
                            aria-haspopup="true"
                            aria-expanded="false"
                            onClick={(e) => handleActiveElement(e, index)}
                          >
                            {title}
                          </Link>
                        </div>
                        <div
                          className={`inner-menu ${
                            index === selectedItem && `active`
                          }`}
                          aria-labelledby="navbarDropdown"
                        >
                          {items.map(({ title, path, show }) => (
                            <div key={title}>
                              {show && (
                                <Link
                                  className="inner-menu-item"
                                  to={`/${path}`}
                                >
                                  {title}
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      </li>
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
      ) : (
        <Skeleton
          count={5}
          width={100}
          height={15}
          containerClassName="sidebar"
          style={{ marginLeft: 50, marginTop: 100 }}
        />
      )}
    </>
  );
};

export default Sidebar;
